import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Check, ChevronDown, Star, ShoppingCart, MapPin, Wine, BookOpen, Gift } from "lucide-react";
import client from "../lib/api";
import { trackAmazonClick, trackEvent } from "../lib/tracking";
import SEO from "../components/SEO";
import LeadMagnetForm from "../components/LeadMagnetForm";
import TestimonialSlider from "../components/TestimonialSlider";
import { Reveal } from "../components/Reveal";

const BENEFITS = [
  { icon: MapPin, title: "Regione per regione", text: "Le mappe del vino italiano commentate: dove nasce ogni grande bottiglia e perché." },
  { icon: Wine, title: "I vitigni autoctoni", text: "Quelli che devi davvero conoscere, spiegati senza paroloni né snobismi." },
  { icon: BookOpen, title: "DOC e DOCG chiare", text: "Denominazioni ed etichette finalmente comprensibili, in parole semplici." },
  { icon: Gift, title: "Aneddoti veri", text: "Storie raccolte in oltre 30 anni dentro le cantine, accanto a chi il vino lo fa." },
];

const FAQS = [
  { q: "La guida è davvero gratuita?", a: "Sì, al 100%. Lasci nome ed email e ricevi subito il PDF via mail: nessun costo, nessun obbligo di acquisto." },
  { q: "Cosa contiene la guida?", a: "I 7 errori più comuni che quasi tutti facciamo quando beviamo vino, e come evitarli da stasera. È il primo assaggio del libro." },
  { q: "E il libro completo?", a: 'Se vuoi approfondire, "Dentro il Calice" è disponibile su Amazon in paperback e Kindle: un viaggio tra le cantine d\'Italia, regione per regione.' },
  { q: "Riceverò tante email?", a: "No spam. Solo contenuti utili sul vino ogni tanto, e puoi disiscriverti quando vuoi con un clic." },
];

function Faq({ items }) {
  const [open, setOpen] = useState(0);
  return (
    <div className="space-y-3" data-testid="lp-faq">
      {items.map((f, i) => (
        <div key={i} className="rounded-xl border border-panna/15 bg-nero-light/40 overflow-hidden">
          <button
            data-testid={`lp-faq-item-${i}`}
            onClick={() => setOpen(open === i ? null : i)}
            className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left"
          >
            <span className="font-serif font-semibold text-panna">{f.q}</span>
            <ChevronDown size={18} className={`text-oro transition-transform duration-300 shrink-0 ${open === i ? "rotate-180" : ""}`} />
          </button>
          <motion.div
            initial={false}
            animate={{ height: open === i ? "auto" : 0, opacity: open === i ? 1 : 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden"
          >
            <p className="px-6 pb-6 text-panna/70 text-sm leading-relaxed">{f.a}</p>
          </motion.div>
        </div>
      ))}
    </div>
  );
}

function LeadCard({ testId }) {
  return (
    <div data-testid={testId} className="rounded-3xl bg-panna p-7 sm:p-9 shadow-2xl shadow-black/40 border border-oro/20">
      <div className="inline-flex items-center gap-2 rounded-full bg-bordeaux/10 px-4 py-1.5 text-bordeaux text-xs font-semibold uppercase tracking-wider">
        <Gift size={14} /> Guida gratuita in PDF
      </div>
      <h3 className="mt-4 font-serif text-2xl sm:text-3xl font-bold text-nero leading-tight">
        7 errori comuni quando bevi vino
      </h3>
      <p className="mt-3 text-nero/70 text-sm leading-relaxed">
        Lascia nome ed email: la ricevi <strong>subito e gratis</strong> nella tua casella. Inizia a bere meglio da stasera.
      </p>
      <div className="mt-6">
        <LeadMagnetForm dark={false} source="lp-libro" />
      </div>
      <p className="mt-4 flex items-center gap-1.5 text-nero/45 text-[11px]">
        <Check size={13} className="text-bordeaux" /> Gratis · niente spam · disiscrizione con un clic
      </p>
    </div>
  );
}

export default function LandingLibro() {
  const [amazon, setAmazon] = useState("https://amzn.eu/d/01pW9tnS");
  const [testimonials, setTestimonials] = useState([]);
  const [downloads, setDownloads] = useState(null);

  useEffect(() => {
    client.get("/settings/public").then((r) => r.data.amazon_url && setAmazon(r.data.amazon_url)).catch(() => {});
    client.get("/testimonials").then((r) => setTestimonials(r.data)).catch(() => {});
    client.get("/leads/count").then((r) => setDownloads(r.data.count)).catch(() => {});
    trackEvent("view_landing_libro");
  }, []);

  const scrollToForm = () => {
    const el = document.getElementById("lead-form");
    if (window.__lenis) window.__lenis.scrollTo(el, { offset: -40 });
    else el?.scrollIntoView({ behavior: "smooth" });
  };

  const AmazonLink = ({ testId, className = "" }) => (
    <a
      href={amazon}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={testId}
      onClick={() => trackAmazonClick("lp-libro")}
      className={`inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full border border-oro/60 px-7 py-3.5 font-semibold text-oro hover:bg-oro/10 transition-colors ${className}`}
    >
      <ShoppingCart size={17} /> Compra il libro su Amazon
    </a>
  );

  return (
    <div data-testid="landing-libro" className="bg-nero text-panna grain">
      <SEO
        title="Dentro il Calice — Guida gratuita"
        path="/lp/libro"
        description="Scarica gratis la guida '7 Errori Comuni quando bevi vino'. Lascia nome ed email e inizia a bere meglio da stasera."
      />

      {/* Wordmark minimale (nessun menu) */}
      <div className="mx-auto max-w-6xl px-5 sm:px-8 pt-8">
        <span className="font-serif text-lg tracking-wide text-oro">Dentro il Calice</span>
      </div>

      {/* HERO — LEAD FIRST */}
      <section className="relative overflow-hidden">
        <div className="mx-auto max-w-6xl px-5 sm:px-8 pt-10 pb-16 sm:pt-14 sm:pb-24 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div>
            <motion.p
              initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
              className="text-oro text-xs sm:text-sm font-semibold uppercase tracking-[0.25em]"
            >
              Guida gratuita · L'Influexpert del vino
            </motion.p>
            <motion.h1
              initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.05 }}
              className="mt-5 font-serif text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] text-panna"
            >
              Bevi vino da anni.<br />
              <span className="text-oro">Ma lo capisci davvero?</span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.12 }}
              className="mt-6 text-panna/75 text-base sm:text-lg leading-relaxed max-w-xl"
            >
              Scarica <strong className="text-panna">gratis</strong> la guida <em>"7 Errori Comuni quando bevi vino"</em>{" "}
              e smetti di sbagliare senza saperlo. Ti bastano nome ed email.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.2 }}
              className="mt-8 flex flex-col sm:flex-row gap-4"
            >
              <button
                data-testid="hero-scroll-form"
                onClick={scrollToForm}
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full bg-oro px-8 py-4 font-semibold text-nero hover:bg-oro-light transition-all hover:-translate-y-0.5 shadow-lg shadow-oro/20"
              >
                <Gift size={18} /> Scarica la guida gratis
              </button>
              <AmazonLink testId="hero-amazon" />
            </motion.div>

            <div className="mt-8 flex items-center gap-4 text-panna/60 text-sm">
              <div className="flex gap-0.5" aria-label="5 stelle">
                {[...Array(5)].map((_, s) => <Star key={s} size={15} className="fill-oro text-oro" />)}
              </div>
              <span>{downloads != null ? `Già scaricata da ${downloads.toLocaleString("it-IT")} appassionati` : "Già scaricata da centinaia di appassionati"}</span>
            </div>
          </div>

          {/* Form primario + copertina */}
          <div id="lead-form" className="relative flex flex-col items-center gap-6">
            <motion.img
              src="/assets/libro-cover.jpg"
              alt="Copertina del libro Dentro il Calice"
              initial={{ opacity: 0, y: -16, rotate: 3 }} animate={{ opacity: 1, y: 0, rotate: 3 }}
              transition={{ duration: 0.8 }}
              className="hidden lg:block w-28 rounded-lg shadow-2xl shadow-black/60 border border-oro/20"
              loading="eager"
            />
            <motion.div
              className="relative z-10 w-full"
              initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.15 }}
            >
              <LeadCard testId="lp-lead-card-hero" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* BENEFICI */}
      <section className="bg-nero-light/40 py-20 sm:py-24 border-y border-panna/10" data-testid="lp-benefits">
        <div className="mx-auto max-w-6xl px-5 sm:px-8">
          <Reveal>
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-panna text-center max-w-2xl mx-auto">
              Cosa imparerai (gratis, in pochi minuti)
            </h2>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {BENEFITS.map((b, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <div className="h-full rounded-2xl border border-panna/10 bg-nero/40 p-7">
                  <div className="h-12 w-12 rounded-full border border-oro/40 flex items-center justify-center text-oro">
                    <b.icon size={22} />
                  </div>
                  <h3 className="mt-5 font-serif text-xl font-semibold text-panna">{b.title}</h3>
                  <p className="mt-2 text-panna/65 text-sm leading-relaxed">{b.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <div className="mt-12 text-center">
            <button
              data-testid="benefits-scroll-form"
              onClick={scrollToForm}
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full bg-oro px-8 py-4 font-semibold text-nero hover:bg-oro-light transition-colors"
            >
              <Gift size={18} /> Voglio la guida gratuita
            </button>
          </div>
        </div>
      </section>

      {/* CHI È STEFANO */}
      <section className="py-20 sm:py-24" data-testid="lp-author">
        <div className="mx-auto max-w-5xl px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-[auto_1fr] gap-10 lg:gap-14 items-center">
          <Reveal>
            <img
              src="/assets/stefano.jpg"
              alt="Stefano Cremonese"
              className="w-56 sm:w-64 aspect-[4/5] object-cover rounded-2xl shadow-xl mx-auto border border-oro/20"
              loading="lazy"
            />
          </Reveal>
          <Reveal delay={0.1}>
            <div>
              <p className="text-oro text-xs font-semibold uppercase tracking-[0.25em]">Chi te la regala</p>
              <h2 className="mt-4 font-serif text-3xl sm:text-4xl font-bold text-panna">Stefano Cremonese</h2>
              <p className="mt-5 text-panna/75 leading-relaxed">
                Figlio d'arte, cresciuto tra le botti e i profumi della cantina di famiglia. Da oltre 30 anni gira le
                cantine d'Italia per raccontare il vino a modo suo: diretto, sincero, senza snobismi. Questa guida è
                il suo modo di darti una mano a partire con il piede giusto.
              </p>
              <p className="mt-4 font-serif text-oro italic">"Il vino è fatto dall'uomo: dietro ogni calice c'è una storia."</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* TESTIMONIANZE */}
      {testimonials.length > 0 && (
        <section className="bg-panna py-20 sm:py-24" data-testid="lp-testimonials">
          <div className="mx-auto max-w-6xl px-5 sm:px-8">
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-nero text-center">
              Cosa dicono di Stefano
            </h2>
            <p className="mt-3 text-nero/60 text-center text-sm">Le parole di chi lo segue e ha imparato con lui.</p>
            <div className="mt-12">
              <TestimonialSlider testimonials={testimonials} />
            </div>
          </div>
        </section>
      )}

      {/* FAQ */}
      <section className="py-20 sm:py-24" data-testid="lp-faq-section">
        <div className="mx-auto max-w-3xl px-5 sm:px-8">
          <h2 className="font-serif text-3xl sm:text-4xl font-bold text-panna text-center">Domande frequenti</h2>
          <div className="mt-10">
            <Faq items={FAQS} />
          </div>
        </div>
      </section>

      {/* CTA FINALE — form gratuito + libro come secondario */}
      <section className="bg-bordeaux py-20 sm:py-24" data-testid="lp-final-cta">
        <div className="mx-auto max-w-5xl px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-panna leading-tight">
              Scarica la guida.<br />È gratis, e cambia il modo in cui bevi.
            </h2>
            <p className="mt-5 text-panna/80 leading-relaxed">
              Lascia nome ed email e ricevi subito il PDF. E se poi vorrai andare più a fondo, il libro completo ti aspetta su Amazon.
            </p>
            <ul className="mt-8 space-y-2 text-panna/80 text-sm">
              {["Guida PDF gratuita, subito via email", "Nessun obbligo di acquisto", "Scritta senza snobismi, per tutti"].map((t, i) => (
                <li key={i} className="flex items-center gap-2"><Check size={16} className="text-oro shrink-0" /> {t}</li>
              ))}
            </ul>
            <div className="mt-8">
              <AmazonLink testId="final-amazon" className="!border-panna/40 !text-panna hover:!bg-panna/10" />
            </div>
          </div>
          <LeadCard testId="lp-lead-card-final" />
        </div>
      </section>

      {/* footer legale minimale */}
      <footer className="bg-nero py-8 text-center text-panna/40 text-xs">
        <p>© {new Date().getFullYear()} Dentro il Calice — Stefano Cremonese</p>
        <p className="mt-2">
          <a href="/privacy-policy" className="underline underline-offset-4 hover:text-oro transition-colors">Privacy Policy</a>
          {" · "}
          <a href="/cookie-policy" className="underline underline-offset-4 hover:text-oro transition-colors">Cookie Policy</a>
        </p>
      </footer>
    </div>
  );
}
